import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";
import toast from "react-hot-toast";
import CloseIcon from "../../../assets/images/employee/closeIcon.svg";
import {
  InputControl,
  InputDatePicker,
  TextareaControl,
} from "../../../components/form/formControls";
import { clientAPI } from "../../../api/client";
import useDebounce from "../../../hooks/useDebounce";

const TZ = "Asia/Ho_Chi_Minh";
const DEFAULT_EXTRA_TIME = 30;
// Nhãn ô ngày cùng cỡ với nhãn các ô khác trong popup (như form Thêm booking)
const DATE_LABEL_STYLE = { fontWeight: 400, lineHeight: "15px", fontSize: "12px", mb: "6px" };

/**
 * Popup thêm / sửa nhân viên được đi muộn (theo form "Thêm xác nhận công và chức danh").
 * Thêm: chọn NV, có hiệu lực ngay (không qua duyệt). Sửa: số phút, thời gian hiệu lực, lý do, ghi chú.
 */
export default function LateCheckinForm({ isOpen, onClose, editItem, onSubmit }) {
  const isEdit = !!editItem;
  const [employee, setEmployee] = useState(null); // { _id, code, fullName }
  const [search, setSearch] = useState("");
  const [extraTime, setExtraTime] = useState(String(DEFAULT_EXTRA_TIME));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [focus, setFocus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setFocus("");
    if (editItem) {
      setEmployee({
        _id: editItem.employeeId,
        code: editItem.employeeObject?.code,
        fullName: editItem.employeeObject?.fullName,
      });
      setExtraTime(String(editItem.extraTime || DEFAULT_EXTRA_TIME));
      setStartDate(moment(editItem.startDate).tz(TZ).toDate());
      setEndDate(editItem.endDate ? moment(editItem.endDate).tz(TZ).toDate() : null);
      setReason(editItem.reason || "");
      setNote(editItem.note || "");
    } else {
      setEmployee(null);
      setExtraTime(String(DEFAULT_EXTRA_TIME));
      setStartDate(moment().startOf("day").toDate());
      setEndDate(null); // trống = vô thời hạn
      setReason("");
      setNote("");
    }
  }, [isOpen, editItem]);

  const { data: found, isFetching } = useQuery({
    queryKey: ["lateCheckinEmployeeSearch", debouncedSearch],
    queryFn: () =>
      clientAPI(
        "get",
        `/employee/search?${new URLSearchParams({
          limit: 8,
          page: 1,
          search: debouncedSearch,
        }).toString()}`
      ),
    enabled: isOpen && !isEdit && !!debouncedSearch.trim(),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const minutes = Number(extraTime);
    if (!employee?._id) return toast.error("Vui lòng chọn nhân viên");
    if (!Number.isInteger(minutes) || minutes <= 0)
      return toast.error("Số phút được đi muộn phải là số nguyên lớn hơn 0");
    if (!startDate) return toast.error("Vui lòng chọn Từ ngày");
    if (endDate && moment(endDate).isBefore(startDate, "day"))
      return toast.error("Đến ngày phải sau hoặc bằng Từ ngày");
    setSubmitting(true);
    try {
      await onSubmit({
        ...(isEdit ? {} : { employeeId: employee._id }),
        extraTime: minutes,
        startDate: moment(startDate).format("YYYY-MM-DD"),
        // trống = vô thời hạn
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        reason: reason.trim(),
        note: note.trim(),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        backgroundColor="var(--color-main)"
        borderRadius={"16px"}
        w={"100%"}
        maxW={"625px"}
      >
        <form onSubmit={handleSubmit}>
          <Flex flexDirection={"column"}>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"10px"}
              padding={"25px 24px"}
              borderBottom={"1px solid var(--bg-line-employee)"}
            >
              <Text
                color="var(--color-header-employee)"
                fontSize={"18px"}
                fontWeight={"600"}
              >
                {isEdit ? "Sửa nhân viên được đi muộn" : "Thêm nhân viên được đi muộn"}
              </Text>
              <Box cursor={"pointer"} onClick={onClose}>
                <Image src={CloseIcon} />
              </Box>
            </Flex>

            <Flex p={"24px"} flexDirection={"column"}>
              <Box w={"100%"} maxW={"545px"} m={"8px auto 24px"}>
                {isEdit ? (
                  <Flex mb={"12px"}>
                    <InputControl
                      label="Nhân viên"
                      value={`${employee?.code || ""} - ${employee?.fullName || ""}`}
                      disabled
                      readonly
                      mwidth={"100%"}
                    />
                  </Flex>
                ) : (
                  <Box mb={"12px"}>
                    <Flex alignItems="center" gap="4px" mb="6px">
                      <Text fontSize={"12px"} color={"var(--color-label-employee)"}>
                        Nhân viên
                      </Text>
                      <Text fontSize={"12px"} color={"red"}>
                        *
                      </Text>
                    </Flex>
                    {employee ? (
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        border="1px solid var(--color-boder)"
                        borderRadius="8px"
                        p="10px 12px"
                      >
                        <Text fontSize="14px" fontWeight={500}>
                          {employee.code} - {employee.fullName}
                        </Text>
                        <Text
                          fontSize="13px"
                          color="var(--color-secondary)"
                          cursor="pointer"
                          onClick={() => setEmployee(null)}
                        >
                          Chọn lại
                        </Text>
                      </Flex>
                    ) : (
                      <>
                        <InputGroup>
                          <Input
                            h="44px"
                            placeholder="Tên NV, mã NV ..."
                            borderRadius="8px"
                            border="1px solid var(--color-boder)"
                            backgroundColor="var(--fill-avatar)"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                          <InputRightElement h="44px">
                            {isFetching ? <Spinner size="sm" /> : <SearchIcon />}
                          </InputRightElement>
                        </InputGroup>
                        {(found?.items || []).length ? (
                          <Box
                            mt="6px"
                            border="1px solid var(--color-boder)"
                            borderRadius="8px"
                            maxH="220px"
                            overflowY="auto"
                          >
                            {found.items.map((emp) => (
                              <Box
                                key={emp._id}
                                p="8px 12px"
                                cursor="pointer"
                                _hover={{ backgroundColor: "var(--fill-avatar)" }}
                                onClick={() =>
                                  setEmployee({
                                    _id: emp._id,
                                    code: emp.code,
                                    fullName: emp.fullName,
                                  })
                                }
                              >
                                <Text fontSize="14px">
                                  {emp.code} - {emp.fullName}
                                </Text>
                              </Box>
                            ))}
                          </Box>
                        ) : null}
                      </>
                    )}
                  </Box>
                )}

                <Flex mb={"12px"}>
                  <InputControl
                    label="Số phút được đi muộn"
                    type="number"
                    value={extraTime}
                    onChange={(e) => setExtraTime(e.target.value)}
                    onFocus={() => setFocus("extraTime")}
                    onBlur={() => setFocus("")}
                    state={focus === "extraTime"}
                    isRequired
                    onlyPositiveNumbers
                    mwidth={"100%"}
                  />
                </Flex>

                <Flex mb={"6px"} gap={"16px"}>
                  <InputDatePicker
                    label="Từ ngày"
                    isRequired
                    selected={startDate}
                    onChange={(d) => {
                      setStartDate(d);
                      if (d && endDate && moment(endDate).isBefore(d, "day")) setEndDate(d);
                    }}
                    onFocus={() => setFocus("startDate")}
                    onBlur={() => setFocus("")}
                    focusStates={focus === "startDate"}
                    dateFormat="dd/MM/yyyy"
                    locale="vi"
                    placeholderText="dd/mm/yyyy"
                    formControlStyle={{ maxW: "100%" }}
                    formLabelStyle={DATE_LABEL_STYLE}
                    zIndex={focus === "startDate" ? 4 : 2}
                  />
                  <InputDatePicker
                    label="Đến ngày"
                    selected={endDate}
                    minDate={startDate}
                    onChange={setEndDate}
                    onFocus={() => setFocus("endDate")}
                    onBlur={() => setFocus("")}
                    focusStates={focus === "endDate"}
                    dateFormat="dd/MM/yyyy"
                    locale="vi"
                    placeholderText="Vô thời hạn"
                    formControlStyle={{ maxW: "100%" }}
                    formLabelStyle={DATE_LABEL_STYLE}
                    zIndex={focus === "endDate" ? 4 : 2}
                  />
                </Flex>
                <Flex mb={"12px"} justifyContent={"space-between"} gap={"8px"}>
                  <Text fontSize={"12px"} color={"var(--color-option-employee-hover)"}>
                    Để trống Đến ngày = áp dụng vô thời hạn.
                  </Text>
                  {endDate ? (
                    <Text
                      fontSize={"12px"}
                      color={"var(--color-secondary)"}
                      cursor={"pointer"}
                      whiteSpace={"nowrap"}
                      onClick={() => setEndDate(null)}
                    >
                      Bỏ ngày kết thúc
                    </Text>
                  ) : null}
                </Flex>

                <Flex mb={"12px"}>
                  <TextareaControl
                    label="Lý do"
                    placeholder="Nhập lý do (không bắt buộc)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onFocus={() => setFocus("reason")}
                    onBlur={() => setFocus("")}
                    state={focus === "reason"}
                    mwidth={"100%"}
                  />
                </Flex>

                <Flex mb={"12px"}>
                  <TextareaControl
                    label="Ghi chú"
                    placeholder="Nhập ghi chú (không bắt buộc)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onFocus={() => setFocus("note")}
                    onBlur={() => setFocus("")}
                    state={focus === "note"}
                    mwidth={"100%"}
                  />
                </Flex>

                {!isEdit ? (
                  <Text fontSize={"12px"} color={"var(--color-option-employee-hover)"}>
                    Nhân viên được thêm ở đây có hiệu lực ngay, không cần duyệt.
                  </Text>
                ) : null}
              </Box>

              <Stack direction="row" spacing={4} align="center" justify={"flex-end"}>
                <Button
                  fontSize={"15px"}
                  fontWeight={"600"}
                  bg="var(--color-main)"
                  w={"112px"}
                  h={"44px"}
                  border={"1px solid var(--color-option-employee-hover)"}
                  color={"var(--color-option-employee-hover)"}
                  _hover={{ filter: "brightness(90%)" }}
                  onClick={onClose}
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  isLoading={submitting}
                  fontSize={"15px"}
                  fontWeight={"600"}
                  color={"var(--color-main)"}
                  bg="var(--linear-gradient-employee)"
                  minW={"112px"}
                  h={"44px"}
                  _hover={{ filter: "brightness(90%)" }}
                >
                  {isEdit ? "Lưu" : "Thêm"}
                </Button>
              </Stack>
            </Flex>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
}
